import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import './CarListings.css';

interface Car {
  uniqueRef?: string;
  url?: string;
  images?: string[];
  manufacturer?: string;
  model?: string;
  modelVersion?: string;
  rawPrice?: number;
  milage?: string;
  fuelType?: string;
  gearbox?: string;
  firstRegistration?: string;
}

function parseRegDate(reg?: string): number {
  if (!reg) return 0;
  const parts = reg.split('/');
  return parseInt(parts[1] || '0', 10) * 12 + parseInt(parts[0] || '0', 10);
}

const gridVariants = {
  hidden: {},
  visible: {},
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const CarIcon = ({ size = 48 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="rgba(0,0,0,0.15)"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l3-4h10l3 4h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="16.5" cy="17.5" r="2.5" />
  </svg>
);

const VehicleCard: React.FC<{ car: Car; eager?: boolean }> = ({ car, eager }) => {
  const year = car.firstRegistration?.slice(-4);
  const subtitle = [car.modelVersion, year].filter(Boolean).join(' · ');

  return (
    <motion.a
      variants={cardVariants}
      href={car.url ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="veh-card"
    >
      <div className="veh-card__img">
        {car.images?.[0] ? (
          <img
            src={car.images[0]}
            alt={`${car.manufacturer} ${car.model}`}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
          />
        ) : (
          <div className="veh-card__no-img">
            <CarIcon />
            <span>Kein Foto verfügbar</span>
          </div>
        )}
      </div>
      <div className="veh-card__body">
        <h3 className="veh-card__name">
          {car.manufacturer} {car.model}
        </h3>
        {subtitle && <p className="veh-card__subtitle">{subtitle}</p>}
        <div className="veh-card__tags">
          {car.milage && <span>{car.milage}</span>}
          {car.fuelType && <span>{car.fuelType}</span>}
          {car.gearbox && <span>{car.gearbox}</span>}
        </div>
        <div className="veh-card__footer">
          {car.rawPrice ? (
            <strong className="veh-card__price">
              € {car.rawPrice.toLocaleString('de-AT')}
            </strong>
          ) : (
            <span className="veh-card__price veh-card__price--na">Preis auf Anfrage</span>
          )}
          <span className="veh-card__cta">Inserat ansehen →</span>
        </div>
      </div>
    </motion.a>
  );
};

const EmptyState: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <div className="veh-empty">
    <CarIcon size={64} />
    <h3 className="veh-empty__heading">Keine Fahrzeuge gefunden</h3>
    <p className="veh-empty__sub">
      Versuchen Sie eine andere Marke oder setzen Sie den Filter zurück.
    </p>
    <button className="veh-empty__reset" onClick={onReset}>
      Filter zurücksetzen
    </button>
  </div>
);

// Module-level cache — survives route changes, cleared on full page reload.
let _cachedCars: Car[] | null = null;
let _fetchPromise: Promise<Car[]> | null = null;

function fetchCars(): Promise<Car[]> {
  if (_cachedCars) return Promise.resolve(_cachedCars);
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = fetch(process.env.REACT_APP_LISTINGS_ENDPOINT || '/api/listings')
    .then((r) => {
      if (!r.ok) throw new Error('Fahrzeuge konnten nicht geladen werden');
      return r.json();
    })
    .then((data: Car[]) => {
      _cachedCars = Array.isArray(data)
        ? data.filter((c) => c && c.url && c.manufacturer)
        : [];
      _fetchPromise = null;
      return _cachedCars;
    })
    .catch((err: Error) => {
      _fetchPromise = null;
      throw err;
    });

  return _fetchPromise;
}

const CarListings: React.FC = () => {
  const [cars, setCars] = useState<Car[]>(() => _cachedCars ?? []);
  const [loading, setLoading] = useState(_cachedCars === null);
  const [error, setError] = useState<string | null>(null);
  const [filterMake, setFilterMake] = useState('');
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    if (_cachedCars) return; // already have data, skip fetch
    fetchCars()
      .then((data) => {
        setCars(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const makes = Array.from(
    new Set(cars.map((c) => c.manufacturer).filter((m): m is string => Boolean(m)))
  );

  const filtered = cars
    .filter((c) => !filterMake || c.manufacturer === filterMake)
    .sort((a, b) => {
      if (sortBy === 'price-asc') return (a.rawPrice ?? 0) - (b.rawPrice ?? 0);
      if (sortBy === 'price-desc') return (b.rawPrice ?? 0) - (a.rawPrice ?? 0);
      if (sortBy === 'newest')
        return parseRegDate(b.firstRegistration) - parseRegDate(a.firstRegistration);
      return 0;
    });

  if (loading) {
    return (
      <div className="veh-page">
        <div className="veh-status">Fahrzeuge werden geladen…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="veh-page">
        <div className="veh-status veh-status--error">{error}</div>
      </div>
    );
  }

  return (
    <div className="veh-page">

      <div className="veh-filterbar">
        <span className="veh-filterbar__count">
          {filtered.length} {filtered.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'} verfügbar
        </span>
        <div className="veh-pills-wrap">
          <div className="veh-pills">
            <button
              className={`veh-pill${filterMake === '' ? ' veh-pill--active' : ''}`}
              onClick={() => setFilterMake('')}
            >
              Alle Marken
            </button>
            {makes.map((m) => (
              <button
                key={m}
                className={`veh-pill${filterMake === m ? ' veh-pill--active' : ''}`}
                onClick={() => setFilterMake(m)}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="veh-sort">
          <span className="veh-sort__label">Sortierung:</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="default">Standard</option>
            <option value="price-asc">Preis aufsteigend</option>
            <option value="price-desc">Preis absteigend</option>
            <option value="newest">Neueste zuerst</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState onReset={() => setFilterMake('')} />
      ) : (
        <motion.div
          key={filterMake + '_' + sortBy}
          className="veh-grid"
          variants={gridVariants}
          initial="hidden"
          animate="visible"
        >
          {filtered.map((car, i) => (
            <VehicleCard key={car.uniqueRef} car={car} eager={i < 6} />
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default CarListings;
